import React, { useRef, useState, useCallback } from 'react';
import { useAtlas } from '../../context/AtlasContext';
import { Location, AtlasTool } from '../../types';
import { MapPinIcon } from '@phosphor-icons/react';

interface LocationBlockProps {
    location: Location;
    isSelected: boolean;
    onSelect: () => void;
    scale: number;
    tool: AtlasTool;
}

const BLOCK_SIZE = 200;

export const LocationBlock: React.FC<LocationBlockProps> = ({
    location,
    isSelected,
    onSelect,
    scale,
    tool
}) => {
    const { updateLocation } = useAtlas();
    const blockRef = useRef<HTMLDivElement>(null);
    const [isDragging, setIsDragging] = useState(false);
    const dragStart = useRef<{ x: number; y: number; startX: number; startY: number } | null>(null);

    const x = location.canvas_x ?? 0;
    const y = location.canvas_y ?? 0;
    const width = location.canvas_width ?? BLOCK_SIZE;
    const height = location.canvas_height ?? BLOCK_SIZE;

    const handlePointerDown = useCallback((e: React.PointerEvent) => {
        if (tool !== 'select') return;

        e.stopPropagation();
        onSelect();

        const element = e.currentTarget as HTMLElement;
        element.setPointerCapture(e.pointerId);

        dragStart.current = {
            x: e.clientX,
            y: e.clientY,
            startX: x,
            startY: y
        };
        setIsDragging(true);
    }, [tool, onSelect, x, y]);

    const handlePointerMove = useCallback((e: React.PointerEvent) => {
        if (!isDragging || !dragStart.current) return;

        const dx = (e.clientX - dragStart.current.x) / scale;
        const dy = (e.clientY - dragStart.current.y) / scale;

        const newX = dragStart.current.startX + dx;
        const newY = dragStart.current.startY + dy;

        // Update location position (optimistic update)
        updateLocation(location.id, {
            canvas_x: newX,
            canvas_y: newY
        });
    }, [isDragging, scale, location.id, updateLocation]);

    const handlePointerUp = useCallback((e: React.PointerEvent) => {
        if (!isDragging) return;

        const element = e.currentTarget as HTMLElement;
        element.releasePointerCapture(e.pointerId);

        setIsDragging(false);
        dragStart.current = null;
    }, [isDragging]);

    // Cursor based on tool
    const getCursor = () => {
        if (tool === 'eraser') return 'cursor-pointer';
        if (tool === 'select') return 'cursor-grab active:cursor-grabbing';
        return 'cursor-default';
    };

    return (
        <div
            ref={blockRef}
            data-block="location"
            className={`absolute group ${getCursor()} ${isDragging ? 'z-50' : 'z-10'}`}
            style={{
                left: x,
                top: y,
                width,
                height,
                transform: isDragging ? 'scale(1.02)' : 'scale(1)',
                transition: isDragging ? 'none' : 'transform 0.15s ease-out'
            }}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onClick={(e) => {
                e.stopPropagation();
                onSelect();
            }}
        >
            {/* Selection Ring / Eraser Highlight */}
            <div
                className={`absolute inset-0 rounded-xl transition-all pointer-events-none ${
                    tool === 'eraser'
                        ? 'ring-2 ring-red-500/50 group-hover:ring-red-500 group-hover:bg-red-500/20'
                        : isSelected
                            ? 'ring-2 ring-indigo-500 ring-offset-2 ring-offset-zinc-950'
                            : 'group-hover:ring-2 group-hover:ring-white/20'
                }`}
            />

            {/* Image */}
            <div className="w-full h-full rounded-xl overflow-hidden bg-zinc-800">
                {location.atlas_image_url ? (
                    <img
                        src={location.atlas_image_url}
                        alt={location.name}
                        className="w-full h-full object-cover"
                        draggable={false}
                    />
                ) : (
                    <div className="w-full h-full flex items-center justify-center bg-zinc-900">
                        <MapPinIcon className="w-12 h-12 text-zinc-700" />
                    </div>
                )}
            </div>

            {/* Name Label */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent rounded-b-xl p-3">
                <div className="flex items-center gap-2">
                    <MapPinIcon className="w-4 h-4 text-indigo-400" weight="fill" />
                    <span className="text-sm font-medium text-white truncate">
                        {location.name}
                    </span>
                </div>
            </div>

            {/* Selection Handles (when selected) */}
            {isSelected && tool === 'select' && (
                <>
                    {/* Corner handles */}
                    <div className="absolute -top-1.5 -left-1.5 w-3 h-3 bg-indigo-500 rounded-full border-2 border-white shadow-lg" />
                    <div className="absolute -top-1.5 -right-1.5 w-3 h-3 bg-indigo-500 rounded-full border-2 border-white shadow-lg" />
                    <div className="absolute -bottom-1.5 -left-1.5 w-3 h-3 bg-indigo-500 rounded-full border-2 border-white shadow-lg" />
                    <div className="absolute -bottom-1.5 -right-1.5 w-3 h-3 bg-indigo-500 rounded-full border-2 border-white shadow-lg" />
                </>
            )}
        </div>
    );
};
